import moment from "moment";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { useToast } from "../../../provider/ToastProvider";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import { FcModal, FieldError, FieldLabel, inputClass } from "../../classes/schedule/ui";
import { ImagePicker, initialsOf, PrimaryButton, ReadOnlyField, SecondaryButton, SettingsCard } from "./shared";

/**
 * Account tab (replaces myProfile/ProfileForm + ChangePassword). Same requests as before:
 * - photo: PUT users/updateProfile { profilePic } right after upload
 * - profile: PUT users/updateProfile { fullName, gender, dob }
 * - password: PUT users/updateProfile { password } (min 8 characters)
 * Login: the username is shown read-only; the password can be changed unless the user signs in with Google / Apple.
 */

const GENDERS = [
  { value: "", title: "Not specified" },
  { value: "Male", title: "Male" },
  { value: "Female", title: "Female" },
  { value: "Other", title: "Other" },
];

const PhoneIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.148a1.5 1.5 0 0 1 1.465 1.175l.513 2.31a1.5 1.5 0 0 1-1.02 1.756l-.97.322a11.04 11.04 0 0 0 5.8 5.8l.322-.97a1.5 1.5 0 0 1 1.756-1.02l2.31.513A1.5 1.5 0 0 1 18 13.352V14.5a1.5 1.5 0 0 1-1.5 1.5H15c-7.18 0-13-5.82-13-13V3.5Z" />
  </svg>
);
const MailIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
    <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" />
    <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
  </svg>
);
const LockIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" />
  </svg>
);

const dobInput = (dob) => (dob && moment(dob).isValid() ? moment(dob).format("YYYY-MM-DD") : "");

export default function AccountTab({ profileSubtitle = "How you appear to your team and members." }) {
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const user = userReduxState.userData || {};

  const initial = React.useMemo(() => ({ fullName: user.fullName || "", gender: user.gender || "", dob: dobInput(user.dob) }), [user.fullName, user.gender, user.dob]);
  const [form, setForm] = React.useState(initial);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);
  // Social login (Google / Apple): no FitClub password, so it can't be changed here.
  const socialType = String(user.socialType || "normal").toLowerCase();
  const isSocial = socialType !== "normal" && socialType !== "";
  const socialName = socialType === "google" ? "Google" : socialType === "apple" ? "Apple" : socialType.charAt(0).toUpperCase() + socialType.slice(1);

  // Pick up fresh user data (the page reloads getUserInfo on open) while nothing is being edited.
  const dirty = form.fullName !== initial.fullName || form.gender !== initial.gender || form.dob !== initial.dob;
  React.useEffect(() => {
    if (!dirty) setForm(initial); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: "" }));
  };

  const savePhoto = async (url) => {
    const res = await axiosInstance.put(`${getUrlForUsers()}updateProfile`, { profilePic: url });
    if (res?.data?.statusCode === 200) {
      dispatch(userActions.setUserData(Object.assign({}, userReduxState.userData, { profilePic: url })));
      addSuccessToast("Photo updated");
    } else {
      addErrorToast(res?.data?.message || "Couldn't update your photo.");
    }
  };

  const save = async () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your name.";
    setErrors(e);
    if (Object.keys(e).length) return;
    const body = {
      fullName: form.fullName.trim(),
      gender: form.gender,
      dob: form.dob ? moment(form.dob, "YYYY-MM-DD").toDate() : user.dob ?? null,
    };
    setSaving(true);
    try {
      const res = await axiosInstance.put(`${getUrlForUsers()}updateProfile`, body);
      if (res?.data?.statusCode === 200) {
        dispatch(userActions.setUserData(Object.assign({}, userReduxState.userData, body)));
        addSuccessToast("Your profile is updated");
      } else {
        addErrorToast(res?.data?.message || "Couldn't save your profile.");
      }
    } catch (err) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <SettingsCard
        title="Your profile"
        subtitle={profileSubtitle}
        footer={
          <>
            {dirty && (
              <button type="button" onClick={() => setForm(initial)} className="rounded-xl px-3 py-2 text-sm font-extrabold text-gray-600 hover:bg-gray-50">
                Discard changes
              </button>
            )}
            <PrimaryButton busy={saving} disabled={!dirty} onClick={save}>
              Save changes
            </PrimaryButton>
          </>
        }
      >
        <div className="mt-5">
          <ImagePicker
            value={user.profilePic}
            fallback={<span className="text-2xl font-black text-gray-600">{initialsOf(user.fullName)}</span>}
            buttonLabel={user.profilePic ? "Change photo" : "Upload photo"}
            onUploaded={savePhoto}
            onError={addErrorToast}
          />
        </div>
        <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="acc-name">Full name</FieldLabel>
            <input id="acc-name" type="text" value={form.fullName} onChange={set("fullName")} className={inputClass(errors.fullName)} />
            <FieldError>{errors.fullName}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="acc-gender" optional>
              Gender
            </FieldLabel>
            <select id="acc-gender" value={form.gender} onChange={set("gender")} className={inputClass(false)}>
              {GENDERS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="acc-dob" optional>
              Date of birth
            </FieldLabel>
            <input id="acc-dob" type="date" value={form.dob} max={moment().format("YYYY-MM-DD")} onChange={set("dob")} className={inputClass(false)} />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Login" subtitle="How you sign in to FitClub on the web and in the app.">
        <div className="mt-3">
          <ReadOnlyField icon={isSocial ? MailIcon : PhoneIcon} label="Username" value={user.username || user.email} hint="Your username can't be changed." />
          <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E9FF] text-indigo-bz">{LockIcon}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-extrabold text-gray-900">Password</div>
              {isSocial ? (
                <div className="text-sm text-gray-600">You sign in with {socialName}, so there's no FitClub password to change. Manage your password in your {socialName} account.</div>
              ) : (
                <div className="text-sm text-gray-600">At least 8 characters</div>
              )}
            </div>
            {!isSocial && <SecondaryButton onClick={() => setPwOpen(true)}>Change password</SecondaryButton>}
          </div>
        </div>
      </SettingsCard>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const { addSuccessToast } = useToast();
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setPw("");
      setPw2("");
      setErrors({});
      setShow(false);
    }
  }, [open]);

  const submit = async () => {
    const e = {};
    if (!pw) e.pw = "Please enter a new password.";
    else if (pw.length < 8) e.pw = "Use at least 8 characters.";
    if (!pw2) e.pw2 = "Please repeat the new password.";
    else if (pw && pw !== pw2) e.pw2 = "The passwords don't match.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await axiosInstance.put(`${getUrlForUsers()}updateProfile`, { password: pw });
      if (res?.data?.statusCode === 200) {
        addSuccessToast("Your password has been changed");
        onClose();
      } else {
        setErrors({ form: res?.data?.message || "Couldn't change your password." });
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal open={open} onClose={onClose} eyebrow="LOGIN" title="Change password" subtitle="Use at least 8 characters." primaryLabel="Update password" onPrimary={submit} busy={busy}>
      {errors.form && (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {errors.form}
        </div>
      )}
      <div>
        <FieldLabel htmlFor="pw-new">New password</FieldLabel>
        <div className="relative">
          <input
            id="pw-new"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            value={pw}
            onChange={(e) => {
              setPw(e.target.value);
              setErrors((x) => ({ ...x, pw: "", form: "" }));
            }}
            className={inputClass(errors.pw) + " pr-20"}
          />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-2 my-auto h-9 rounded-xl px-3 text-xs font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
            {show ? "Hide" : "Show"}
          </button>
        </div>
        <FieldError>{errors.pw}</FieldError>
      </div>
      <div>
        <FieldLabel htmlFor="pw-repeat">Repeat new password</FieldLabel>
        <input
          id="pw-repeat"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={pw2}
          onChange={(e) => {
            setPw2(e.target.value);
            setErrors((x) => ({ ...x, pw2: "", form: "" }));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          className={inputClass(errors.pw2)}
        />
        <FieldError>{errors.pw2}</FieldError>
      </div>
    </FcModal>
  );
}
